import React from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import CodeEditor, { ICodeEditorHandle } from './CodeEditor';

interface IJsonEditorProps {
    /** The current value to edit. When this changes upstream, the editor reloads from it. */
    value: unknown;
    /** Called with the parsed object when the user applies a valid edit. */
    onApply: (parsed: any) => void;
    applyLabel?: string;
    /** Status line shown when there is a valid, unsaved change. */
    dirtyNote?: string;
    /** Extra action buttons (e.g. persistence) rendered next to Apply; receives the current text + validity. */
    renderExtraActions?: (text: string, valid: boolean) => React.ReactNode;
    /** Ctrl/Cmd+S in the editor (e.g. download); without it the key is the browser's. */
    onSaveKey?: (text: string, valid: boolean) => void;
}

export interface IJsonEditorHandle {
    /** Puts `text` in the editor as an edit (e.g. a loaded file); the upstream value is untouched. */
    load: (text: string) => void;
    /** The editor's text now, applied or not. */
    text: () => string;
}

const stringify = (v: unknown): string => (v == null ? '' : JSON.stringify(v, null, 2));

/** Where JSON.parse stopped, from its message: "line L column C" (Firefox, newer V8) or "position P" (V8). */
function errorOffset(text: string, message: string): number | null {
    const lineColumn = /line (\d+) column (\d+)/.exec(message);
    if (lineColumn) {
        const lines = text.split('\n').slice(0, Number(lineColumn[1]) - 1);
        return lines.reduce((sum, line) => sum + line.length + 1, 0) + Number(lineColumn[2]) - 1;
    }
    const position = /position (\d+)/.exec(message);
    return position ? Number(position[1]) : null;
}

/**
 * Read/edit a JSON value with live validation (M2). No persistence — applying only hands the
 * parsed object to `onApply` (e.g. DatabaseContext.updateScripts) for an in-memory live preview.
 * Valid JSON is re-indented when the editor loses focus. Ctrl+Enter applies.
 */
const JsonEditor = React.forwardRef<IJsonEditorHandle, IJsonEditorProps>(({
    value,
    onApply,
    applyLabel = 'Alkalmaz',
    dirtyNote = 'Nem mentett változás — csak élő előnézet, az adatbázisba nem kerül mentésre.',
    renderExtraActions,
    onSaveKey,
}, ref) => {
    const initial = React.useMemo(() => stringify(value), [value]);
    const [text, setText] = React.useState(initial);
    const [error, setError] = React.useState<string | null>(null);
    const editor = React.useRef<ICodeEditorHandle>(null);
    const textRef = React.useRef(text);
    textRef.current = text;

    // Reload when the upstream value changes (product switch, reload, or after an applied edit).
    React.useEffect(() => {
        setText(stringify(value));
        setError(null);
    }, [value]);

    const validate = (next: string) => {
        if (next.trim() === '') {
            setError(null);
            return;
        }
        try {
            JSON.parse(next);
            setError(null);
        } catch (e: any) {
            setError(e?.message ?? 'Érvénytelen JSON');
        }
    };

    const handleChange = (next: string) => {
        setText(next);
        validate(next);
    };

    React.useImperativeHandle(ref, () => ({ load: handleChange, text: () => textRef.current }), []);

    const handleBlur = () => {
        if (text.trim() === '') return;
        try {
            const formatted = stringify(JSON.parse(text));
            if (formatted !== text) setText(formatted);
        } catch {
            // invalid: left as typed, the error line says why
        }
    };

    const handleApply = () => {
        try {
            onApply(JSON.parse(text));
            setError(null);
        } catch (e: any) {
            setError(e?.message ?? 'Érvénytelen JSON');
        }
    };

    const dirty = text !== initial;
    const offset = error ? errorOffset(text, error) : null;

    return (
        <Stack spacing={1}>
            <CodeEditor
                ref={editor}
                value={text}
                error={!!error}
                onChange={handleChange}
                onBlur={handleBlur}
                onApplyKey={() => { if (!error && dirty) handleApply(); }}
                onSaveKey={onSaveKey && (() => onSaveKey(text, !error))}
            />
            <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ minHeight: 18 }}>
                {error
                    ? <>
                        JSON hiba: {error}
                        {offset !== null && (
                            <> · <Link component="button" variant="caption" onClick={() => editor.current?.focusAt(offset)}>
                                ugrás a hibához
                            </Link></>
                        )}
                    </>
                    : (dirty ? dirtyNote : 'Érvényes JSON')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="contained" size="small" disabled={!!error || !dirty} onClick={handleApply}
                    title="Ctrl+Enter">
                    {applyLabel}
                </Button>
                <Button variant="text" size="small" disabled={!dirty} onClick={() => { setText(initial); setError(null); }}>
                    Visszaállítás
                </Button>
                {renderExtraActions?.(text, !error)}
            </Stack>
        </Stack>
    );
});

export default JsonEditor;
